import { Tab } from '@headlessui/react';
import * as React from 'react';
import { Typography } from '../typography';
import clsx from 'clsx';
import { Icon } from '../icon';
import Icons from '@/const/icons';
import { Check } from 'lucide-react';

import { ChannelTabList, ErrorIcon } from '@/components/dashboard';
import { DropdownTab } from '../../common/tabs/dropdown-tab/DropdownTab';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';
import { TabListPreview } from '@/interfaces';
import { TooltipComponent } from '../tooltip';

interface TabCustomProp {
	channelItem: ChannelTabList;
	setItemSelected: (value: string) => void;
	itemSelected: string;
	className?: string;
	onClickTab?: () => void;
	onClick?: () => void;
}

export const SocialMediaTabsCustom: React.FC<TabCustomProp> = ({
	onClick,
	className,
	// onClickTab,
	itemSelected,
	channelItem,
	setItemSelected,
}) => {
	const router = useRouter();
	const { id, tab, search, applyToAll } = router.query;
	const { approved, channels, name } = channelItem;
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const dropdownRef = React.useRef<HTMLDivElement>(null);
	const [selectedOption, setSelectedOption] = React.useState<
		TabListPreview | undefined
	>();

	const channelHasError = channelItem.hasError;

	React.useEffect(() => {
		function handleClickOutside(event: MouseEvent): void {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, dropdownRef]);

	React.useEffect(() => {
		if (typeof tab === 'string') {
			channels.forEach((channel) => {
				if (channel.name === tab) {
					setItemSelected(channel.name);
					setSelectedOption(channel);
				}
			});
		}
	}, [tab]);

	const handleTabClick = (): void => {
		if (channelItem.name !== 'email') {
			setIsOpen((prev) => !prev);
		}

		const isSelectedInChannels = channels.some(
			(channel) => channel.name === (tab as string)
		);

		if (isSelectedInChannels) return;

		router.push({
			pathname: `${AppRoutes.LAWYERAPP_MARKETING_CAMPAIGN}/${id}`,
			query: {
				tab: channels[0].name,
				...(search && { search }),
				...(applyToAll && { applyToAll }),
			},
		});

		setSelectedOption(channels[0]);
		setItemSelected(channels[0].name);
	};

	return (
		<div className="relative" ref={dropdownRef}>
			<Tab
				onClick={handleTabClick}
				className={clsx(
					'min-w-[174px] h-14 rounded-t-2xl py-2 px-6 flex items-center gap-2.5 outline-none justify-center',
					className
				)}
			>
				<div>
					{channelHasError && <ErrorIcon />}
					{approved && !channelHasError && (
						<span className="rounded-full bg-purple-150 size-5 flex items-center justify-center">
							<Check color="#FFF" width={12} height={12} className="mt-0.5" />
						</span>
					)}
					{!approved && !channelHasError && (
						<TooltipComponent
							className="w-[185px]"
							classNameLabel="!text-left"
							textType="semibold-12"
							trigger={
								<div>
									<Icon
										src={Icons.infoCircle}
										className="!size-5 text-main-orange"
									/>
								</div>
							}
							label="Copies of this channel have not yet approved"
						/>
					)}
				</div>
				<div className="flex flex-col items-start">
					<Typography type="semibold-14" className="text-purple-150 capitalize">
						{name}
					</Typography>
					{channels.some((channel) => channel.name === itemSelected) &&
						itemSelected !== 'marketing_email' && (
							<Typography type="semibold-12" className="text-black capitalize">
								{selectedOption?.name.split('_').at(0)}
							</Typography>
						)}
				</div>

				{channelItem.name !== 'email' && (
					<button onClick={onClick}>
						<Icon src={Icons.arrowDown} className="!size-6 text-black" />
					</button>
				)}
			</Tab>
			<DropdownTab
				dropdownRef={dropdownRef}
				channels={channels}
				itemSelected={selectedOption?.name || ''}
				handleSelectOption={(item) => {
					setSelectedOption(item);
					setItemSelected(item.name as string);
				}}
				isDropdownOpen={isOpen}
			/>
		</div>
	);
};
